import { GridRowModel } from '@mui/x-data-grid'
import { LicenseInfo } from '@mui/x-license-pro'
import { Grid } from '.'
import { useCallback, useContext, useEffect, useReducer, useRef } from 'react'
import { Button } from '@mui/material'
import { ToastMessage } from '../../context/ToastPopUpContext'
import { Save, SearchIcon } from 'lucide-react'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { useGridApiRef, Toolbar } from '@mui/x-data-grid-pro'
import AddBoxIcon from '@mui/icons-material/AddBox'

LicenseInfo.setLicenseKey(
    '78289722ce54bd59055e8887bc9a39daTz0xMjcwMzksRT0xODA1MzI3OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='
)

interface Props {
    data: any
    visibleColumns: any
    defaultColumns?: any
    columnsAvailable: any
    customColumnNames?: string[]
    columnWidths?: {
        [key: string]: number
    }
    apiColumns?: any
    types?: {
        [key: string]: number
    }
    updatedRow: (onRowUpdate: any) => void
    customButtonAddShow?: boolean
    customButtonAdd?: () => void
    customButtonAddName?: string
}

export const UniversalEditableDataTable = ({
    data,
    visibleColumns,
    columnsAvailable,
    customColumnNames,
    defaultColumns,
    columnWidths,
    apiColumns,
    updatedRow,
    types,
    customButtonAddShow,
    customButtonAdd,
    customButtonAddName,
}: Props) => {
    const defaultState = {
        columns: [],
        rows: [],
        newRows: [],
        loading: false,
        defaultRows: [],
        saveTriggered: false,
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)
    const { rows, columns, newRows, loading, defaultRows, saveTriggered } =
        state

    const apiRef: any = useGridApiRef()

    const search = () => {
        apiRef?.current?.showFilterPanel?.()
    }

    const handleSave = useCallback(() => {
        try {
            setState({ loading: true })

            if (!newRows || newRows.length === 0) {
                const hasChanges = rows.some((row) => {
                    const updated = newRows.find((u) => u.id === row.id)
                    return (
                        updated &&
                        JSON.stringify(updated) !== JSON.stringify(row)
                    )
                })

                if (!hasChanges) {
                    setToastMessage('No changes to save', 'info')
                    return
                }
            }

            const mergedRows = rows.map((row: any) => {
                const updated = newRows.find((u: any) => u.id === row.id)
                return updated ? updated : row
            })

            setState({ rows: mergedRows, newRows: [], saveTriggered: true })
            updatedRow(mergedRows)
            setToastMessage('Changes Have be Saved', 'success')

            setState({ newRows: [] })
        } catch (error: any) {
            setToastMessage('Failed to save changes', 'error')
            console.error('Error saving changes:', error)
        } finally {
            setState({ loading: false })
        }
    }, [rows, newRows, updatedRow])

    const handleCancel = useCallback(() => {
        setState({ newRows: [], rows: defaultRows })
        setToastMessage('Changes discarded', 'info')
    }, [])

    const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
        try {
            setState({ loading: true })

            const updatedList = [...newRows]

            const existingIndex = updatedList.findIndex(
                (r) => r.id === newRow.id
            )

            if (existingIndex !== -1) {
                updatedList[existingIndex] = newRow
            } else {
                updatedList.push(newRow)
            }

            setState({ newRows: updatedList })

            return newRow
        } catch (error: any) {
            setToastMessage('Failed to edit row', 'error')
            console.error('Error editing row:', error)
            return oldRow
        } finally {
            setState({ loading: false })
        }
    }
    useEffect(() => {
        if (data && data?.length !== 0) {
            let filteredColumns: any

            if (visibleColumns && visibleColumns?.length !== 0) {
                filteredColumns = Object.keys(data[0])
                    .map((key: string) => {
                        const visibleCol = visibleColumns.find(
                            (col: any) => col.field === key
                        )
                        return {
                            field: key,
                            headerName: key?.toUpperCase(),
                            width: 150,
                            editable: true,

                            type: 'string',
                            ...(visibleCol || {}),
                            ...(columnWidths && columnWidths[key]
                                ? { width: columnWidths[key] }
                                : {}),
                            ...(types && types[key]
                                ? { type: types[key] }
                                : {}),
                        }
                    })
                    .filter(
                        (key) =>
                            visibleColumns.some(
                                (col: any) => col.field === key.field
                            ) && key.field
                    )

                if (saveTriggered && saveTriggered === true) {
                    const filteredRows =
                        rows?.length > 1
                            ? rows
                            : data?.map((values) => ({
                                  ...values,
                                  id: values?.id,
                              }))
                    if (customColumnNames && customColumnNames?.length !== 0) {
                        filteredColumns = filteredColumns?.map(
                            (column, index) => ({
                                ...column,
                                headerName:
                                    customColumnNames[index]?.toUpperCase(),
                            })
                        )
                    }

                    return setState({
                        columns: filteredColumns,
                        rows: filteredRows,
                        defaultRows: filteredRows,
                    })
                }
                const filteredRows = data?.map((values) => ({
                    ...values,
                    id: values?.id,
                }))

                if (customColumnNames && customColumnNames?.length !== 0) {
                    filteredColumns = filteredColumns?.map((column, index) => ({
                        ...column,
                        headerName: customColumnNames[index]?.toUpperCase(),
                    }))
                }

                return setState({
                    columns: filteredColumns,
                    rows: filteredRows,
                    defaultRows: filteredRows,
                })
            }
        }
    }, [data, visibleColumns, columnsAvailable])

    const CustomToolbar = () => {
        return (
            <Toolbar
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 10,
                    gap: 1,
                }}
            >
                <Grid size={12}>
                    {customButtonAddShow && (
                        <Button
                            onClick={customButtonAdd}
                            title="Add"
                            sx={{ color: 'primary' }}
                            startIcon={<AddBoxIcon />}
                        >
                            Add {customButtonAddName}
                        </Button>
                    )}
                    <Button
                        onClick={search}
                        title="Search"
                        sx={{ color: 'primary' }}
                        startIcon={<SearchIcon />}
                    >
                        Search
                    </Button>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={handleSave}
                        disabled={loading}
                        startIcon={<Save />}
                        sx={{ mr: 1 }}
                    >
                        Save Table Changes
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Grid>
            </Toolbar>
        )
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={12}>
                <DataGridPro
                    rows={rows}
                    apiRef={apiRef ? apiRef : null}
                    columns={columns}
                    editMode="row"
                    processRowUpdate={processRowUpdate}
                    onCellEditStop={(params, event) => {
                        apiRef.current.stopRowEditMode({
                            id: params.id,
                            ignoreModifications: false,
                        })
                    }}
                    onProcessRowUpdateError={(error) =>
                        setToastMessage(`Error: ${error}`, 'warning')
                    }
                    loading={loading}
                    getRowId={(row) => row.id}
                    showToolbar={true}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    pagination
                    disableColumnMenu={true}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 50,
                            },
                        },
                    }}
                />
            </Grid>
        </Grid>
    )
}
